import React from "react";
import ReactDOM from "react-dom";
import reportWebVitals from "./reportWebVitals";
// import { Tooltip, Toast, Popover } from "bootstrap";
import { Auth0Provider } from "@auth0/auth0-react";
import { Router } from "@reach/router";
import "intersection-observer";

import { QueryClient, QueryClientProvider } from "react-query";

import "./index.scss";
import "./config/window";
import "./config/i18n";
import Protected from "./Protected";
import App from "./App/App";
import Obsolete from "./Components/Obsolete";

const {
  REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_AUTH0_DOMAIN,
  REACT_APP_AUTH0_AUDIENCE
} = process.env;

const queryClient = new QueryClient();

const Loader = () => (
  <div className="vh-100 p-5 d-flex justify-content-center align-items-center">
    <div className="spinner-border" role="status"></div>
  </div>
);

// Bug real (auditoría 2026-08-16): este callback reemplaza el default del
// SDK de Auth0, que además de lo propio limpia ?code=...&state=... de la
// URL (window.history.replaceState). Sin esa limpieza los params OAuth
// quedaban visibles/copiables en la barra de direcciones, y un refresh con
// ellos todavía ahí podía hacer que el SDK reintentara procesar un code ya
// consumido. Exportada para poder testear la lógica sin montar todo el
// árbol de index.js.
export const onRedirectCallback = (appState) => {
  window.history.replaceState({}, document.title, appState?.returnTo || window.location.pathname)
  sessionStorage.setItem('just_logged_in', 'true')
}

// Auth0Provider sin cacheLocation/useRefreshTokens guarda el token SOLO en
// memoria — sobrevive la navegación normal (@reach/router es client-side)
// pero se pierde en cualquier carga de página completa (F5, un link nuevo,
// el QR de Escriba). Para recuperarse ahí, el SDK depende de un iframe de
// silent-auth contra el dominio de Auth0 — una cookie de tercero que los
// browsers vienen bloqueando cada vez más. Detalle completo, y por qué NO
// se activa esto para usuarios reales (mismo trade-off de seguridad ya
// descartado para cacheLocation='localstorage' sin acotar), en
// AUTH0_SESSION_BUG_REPORT.md — Opción A (Custom Domain propio para el
// tenant de QA) se descartó explícitamente por el equipo.
//
// Esta cookie la pone e2e/auth.setup.js ANTES del primer login, nunca la
// UI ni ningún flujo real — sin ella (el caso de cualquier usuario real,
// en cualquier entorno) el comportamiento es exactamente el de siempre
// (memoria, sin refresh tokens). Alguien podría setearla manualmente en su
// propio browser vía devtools, pero eso sólo cambia CÓMO SE CACHEA SU
// PROPIA sesión en SU PROPIO dispositivo — no expone ni afecta a nadie más.
const E2E_AUTH_CACHE_COOKIE = 'e2e_auth_cache=playwright-e2e-1'
export const shouldPersistAuthForE2E = () =>
  document.cookie.split('; ').includes(E2E_AUTH_CACHE_COOKIE)

const Main = () => {
  let redirectUri = window.location.origin
  try {
    let searchParams = new URLSearchParams(window.location.search)
    if (!searchParams.get('next')) {
      // pathname + search, no solo pathname: un deep link con querystring
      // perdía todo lo que hubiera después del `?` en el PRIMER login de
      // cada dispositivo, y `next` volvía a una URL sin el parámetro que
      // el destino necesitaba (el canal mobile de Escriba lo iba a
      // necesitar; el bug es general, no específico de esa feature).
      // Hallazgo de la auditoría de deploy-apagado, 2026-08-24 — corrige
      // el login de TODOS los usuarios.
      let target = `${window.location.pathname}${window.location.search}`
      let params = new URLSearchParams({ next: target })
      if (params.get('next')) {
        redirectUri = `${window.location.origin}?${params.toString()}`
      }
    }
  } catch {}

  const persistAuthForE2E = shouldPersistAuthForE2E()

  return (
    <React.Suspense fallback={<Loader />}>
      {window.Modernizr.es6object ? (
        <Auth0Provider
          domain={REACT_APP_AUTH0_DOMAIN}
          clientId={REACT_APP_AUTH0_CLIENT_ID}
          redirectUri={redirectUri}
          audience={REACT_APP_AUTH0_AUDIENCE}
          onRedirectCallback={onRedirectCallback}
          cacheLocation={persistAuthForE2E ? 'localstorage' : 'memory'}
          useRefreshTokens={persistAuthForE2E}
        >
          <QueryClientProvider client={queryClient}>
            <Protected>
              <Router>
                <App default />
              </Router>
            </Protected>
          </QueryClientProvider>
        </Auth0Provider>
      ) : (
        <Obsolete />
      )}
    </React.Suspense>
  );
}


ReactDOM.render(<Main />, document.getElementById("root"));

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
